// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import { createReference } from '@medplum/core';
import type { Coding, Encounter, Patient, Practitioner, QuestionnaireResponse } from '@medplum/fhirtypes';
import { botIdentifier } from '../lib/bot-identifier';

const ONBOARDING_ID_SYSTEM = 'http://polarissleep.com/onboarding-id';
const TAG_SYSTEM = 'http://polarissleep.com/tags';
const BOOKED_CODE = 'eligibility-appointment-booked';
const PAGE_1_CODE = 'eligibility-check-page-1';
const SLEEP_HISTORY_CODE = 'sleep-history';

export type IntakePhase = 'eligibility-check-page-1' | 'sleep-history';

export const INTAKE_PHASES: readonly IntakePhase[] = ['eligibility-check-page-1', 'sleep-history'] as const;

export interface SubmitNpIntakeStepInput {
  medplum: MedplumClient;
  patient: Patient;
  encounter: Encounter;
  practitioner: Practitioner;
  phase: IntakePhase;
  response: QuestionnaireResponse;
  epworthResponse?: QuestionnaireResponse;
  stopBangResponse?: QuestionnaireResponse;
  /** True only for the first call in the four-step sequence. */
  isFirstStep: boolean;
}

export interface SubmitNpIntakeStepResult {
  success: boolean;
  nextPhase: string;
}

/**
 * Drives one step of the four-questionnaire NP intake flow.
 *
 * The patient enters at `eligibility-appointment-booked` or
 * `eligibility-appointment-pending`. `process-eligibility-step` enforces
 * strict phase-tag matching, so we patch either tag to
 * `eligibility-check-page-1` before the first call.
 * The bot advances the tag itself on each successful step.
 *
 * Failure modes:
 *   - First step bot-failure -> revert the tag to its original scheduled-intake
 *     tag so the NP can retry from a clean state. Rollback is best-effort; if
 *     it also fails the operator can repair the tag in Medplum.
 *   - Mid-flow bot-failure -> surface the error, leave the patient at the
 *     current intermediate tag. The NP can resume from that step on retry.
 */
export async function submitNpIntakeStep({
  medplum,
  patient,
  encounter,
  practitioner,
  phase,
  response,
  isFirstStep,
  epworthResponse,
  stopBangResponse,
}: SubmitNpIntakeStepInput): Promise<SubmitNpIntakeStepResult> {
  if (!patient.id) {
    throw new Error('Patient is missing id; cannot submit intake step.');
  }
  const uniqueId = readUniqueId(patient);
  if (!uniqueId) {
    throw new Error(`Patient ${patient.id} has no onboarding-id identifier; cannot resolve uniqueId for bot call.`);
  }

  let didPatchTag = false;
  let patchedFromTag = BOOKED_CODE;
  if (isFirstStep) {
    patchedFromTag = readFirstStepTag(patient) ?? BOOKED_CODE;
    await advancePatientTag(medplum, patient.id, patchedFromTag, PAGE_1_CODE);
    didPatchTag = true;
  }

  try {
    const qrForBot: QuestionnaireResponse = {
      ...response,
      resourceType: 'QuestionnaireResponse',
      author: createReference(practitioner),
      source: createReference(patient),
      encounter: createReference(encounter),
    };

    const result = (await medplum.executeBot(
      botIdentifier('process-eligibility-step'),
      {
        uniqueId,
        phase,
        ...(phase === SLEEP_HISTORY_CODE
          ? {
              questionnaireResponseEpworth: epworthResponse,
              questionnaireResponseStopBang: stopBangResponse,
            }
          : { questionnaireResponse: qrForBot }),
      },
      'application/json'
    )) as SubmitNpIntakeStepResult;

    return result;
  } catch (err) {
    if (didPatchTag) {
      // Best-effort rollback. Swallow rollback errors so the original
      // bot-call error surfaces to the caller.
      try {
        await advancePatientTag(medplum, patient.id, PAGE_1_CODE, patchedFromTag);
      } catch {
        // Operator can repair the scheduled-intake tag in Medplum if this also fails.
      }
    }
    throw err;
  }
}

function readFirstStepTag(
  patient: Patient
): 'eligibility-appointment-booked' | 'eligibility-appointment-pending' | undefined {
  const code = patient.meta?.tag?.find((tag) => tag.system === TAG_SYSTEM)?.code;
  return code === BOOKED_CODE || code === 'eligibility-appointment-pending' ? code : undefined;
}

function readUniqueId(patient: Patient): string | undefined {
  return patient.identifier?.find((i) => i.system === ONBOARDING_ID_SYSTEM)?.value;
}

/**
 * Replace a Polaris-tag entry on Patient.meta.tag. Reads the current patient,
 * filters out the `from` tag, adds the `to` tag, and writes the resource back.
 */
async function advancePatientTag(medplum: MedplumClient, patientId: string, from: string, to: string): Promise<void> {
  const patient: Patient = await medplum.readResource('Patient', patientId);
  const tags = patient.meta?.tag ?? [];
  const nextTags = tags.filter((t: Coding) => !(t.system === TAG_SYSTEM && t.code === from));
  nextTags.push({ system: TAG_SYSTEM, code: to });
  await medplum.updateResource({
    ...patient,
    meta: { ...(patient.meta ?? {}), tag: nextTags },
  });
}
